import {AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {DialogComponentInterface} from '../dialog-component.interface';
import {MAT_DIALOG_DATA, MatDialogConfig} from '@angular/material';
import {DialogConfigBase} from '../dialog-config-base';
import {DialogConfigInterface} from '../dialog-config.interface';
import {TranslationContainerComponent} from '../../abstract/translation-container.component';
import {InsuranceCompanyOffersState, OfferDialogService} from '../../../../core/services/impl/offer-dialog.service';
import {ApplicationStateService} from '../../../services/application-state.service';
import {AnimationBuilder} from '@angular/animations';
import {OffersProgressDialogAnimationBuilder} from './offers-progress-dialog-animation-builder';
import {isCarOne, isNestBank, isYANOSIK, salesChannel} from '../../../../core/utils/utils';
import {SalesChannel} from '../../../../core/consts/sales-channel.enum';

@Component({
  selector: 'app-offers-progress-dialog',
  templateUrl: './offers-progress-dialog.component.html',
})
export class OffersProgressDialogComponent extends TranslationContainerComponent implements OnInit, AfterViewInit, DialogComponentInterface {

  @ViewChild('animatableElement') animatableElement: ElementRef;
  @ViewChild('dynamicLine') dynamicLine: ElementRef;

  logoUrl: string;
  partnerLogoUrl: string;
  logosVisible: boolean = !isCarOne();
  animationImages: OffersProgressDialogAnimationImage[];
  animationVisible: boolean = !isCarOne();
  logosDividerVisible: boolean = isNestBank();

  private offerDialogService: OfferDialogService;
  private animationDuration: number;
  private animationImagesDir: string;

  private readonly PARTNER_LOGOS: OfferDialogPartnerLogo[] = [
    {
      salesChannel: SalesChannel.NESTBANK,
      logoUri: './assets/images/logo/nestbank-logo.svg'
    }
  ];

  static getConfigBuilder(): any {
    return new OffersProgressDialogConfig();
  }

  get insuranceCompaniesOffersState(): InsuranceCompanyOffersState[] {
    return this.offerDialogService.insuranceCompaniesOffersState;
  }

  get offersAmount(): number {
    return this.stateService.displayedOffersNumber;
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
              private stateService: ApplicationStateService,
              private animationBuilder: AnimationBuilder) {
    super('OFFERS_PROGRESS_DIALOG');
  }

  ngOnInit() {
    this.initLogo();
    this.initPartnerLogo();
    this.animationImagesDir = this.getAnimationImagesDir();
    this.createAnimationImages();
    this.offerDialogService = this.data.offerDialogService;
    this.animationDuration = this.offerDialogService.dialogDisplayTime;
  }

  ngAfterViewInit() {
    if (this.animationVisible) {
      const animationBuilder = new OffersProgressDialogAnimationBuilder(this.animationBuilder);
      animationBuilder.init(this.animatableElement, this.dynamicLine, this.animationDuration);

      if (this.offerDialogService.animationAccelerationEnabled) {
        this.offerDialogService.dialogClosingEvent.take(1).subscribe(() => {
          animationBuilder.accelerate();
        });
      }
    }
  }

  getAnimationImagePath(filename: string): string {
    return `${this.animationImagesDir}/${filename}`;
  }

  private initLogo(): void {
    if (isYANOSIK()) {
      this.logoUrl = './assets/images/logo/yanosik-black.svg';
    } else {
      this.logoUrl = './assets/images/logo/kioskpolis-rgb.svg';
    }
  }

  private initPartnerLogo(): void {
    const partnerLogo = this.PARTNER_LOGOS.find(config => config.salesChannel === salesChannel());
    this.partnerLogoUrl = partnerLogo ? partnerLogo.logoUri : null;
  }

  private getAnimationImagesDir(): string {
    switch (salesChannel()) {
      case SalesChannel.NESTBANK:
        return './assets/images/offers-progress-animation/nest-bank';
      default:
        return './assets/images/offers-progress-animation';
    }
  }

  private createAnimationImages(): void {
    if (this.animationVisible) {
      this.animationImages = [
        {
          filepath: this.getAnimationImagePath('car.svg'),
          cssSuffix: 'car',
          animatableElement: true
        },
        {
          filepath: this.getAnimationImagePath('hydrant.svg'),
          cssSuffix: 'hydrant'
        },
        {
          filepath: this.getAnimationImagePath('house.svg'),
          cssSuffix: 'house'
        },
        {
          filepath: this.getAnimationImagePath('tree.svg'),
          cssSuffix: 'tree'
        },
        {
          filepath: this.getAnimationImagePath('finish-line.svg'),
          cssSuffix: 'finish-line'
        },
      ];
    }
  }

}

export class OffersProgressDialogConfig extends DialogConfigBase implements DialogConfigInterface {

  private config: MatDialogConfig = {
    panelClass: 'offers-progress-dialog-panel'
  };

  constructor() {
    super();
  }

  buildConfiguration(data: any): MatDialogConfig {
    return this.buildConfig(data, this.config);
  }
}

export interface OffersProgressDialogAnimationImage {
  filepath: string;
  cssSuffix: string;
  animatableElement?: boolean;
}

export interface OfferDialogPartnerLogo {
  salesChannel: SalesChannel;
  logoUri: string;
}

